import { useEffect } from "react"
import { createPortal } from "react-dom"
import styled from "styled-components"

import { useLazyGetReordersListQuery } from "store/api/adminApi"
import { ApiTypes, UITypes } from "types"
import styles from "assets/scss/index.module.scss"

import useExportCSV from "hooks/useExportCSV"
import { ModalLayout } from "layouts/ModalLayout"
import { ModalHeader } from "components/common/ModalHeader"
import { ButtonNew } from "components/form/ButtonNew"

const ButtonWrapper = styled.div`
  span {
    position: absolute;
    top: 9px;
    right: 45px;
    cursor: pointer;
    z-index: 100;
  }
`

const ModalContent = styled.div`
  padding: 15px;
  display: flex;
  flex-direction: column;
  max-height: 440px;
`

const Header = styled.div`
  font-size: 14px;
  color: #64748b;
  position: sticky;
  top: 0;
  background-color: #fff;
  padding: 0 0 20px;

  div {
    padding: 0;
    span {
      font-weight: 400;
      color: #64748b;
    }
  }
`

const Row = styled.div`
  display: flex;
  gap: 5px;
  width: 100%;
  padding: 20px 0;

  border-bottom: 1px solid #e0e0e0;

  &:nth-child(2) {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: none;
  }

  span {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    color: #0f172a;
    font-weight: 500;
  }

  span:first-child {
    flex: 5;
  }

  span:nth-child(2) {
    flex: 2;
  }

  span:nth-child(3) {
    flex: 2;
  }

  span:last-child {
    flex: 3;
  }
`

const ListContent = styled.div`
  overflow-y: auto;
  flex: 1;
  max-height: calc(90vh - 140px);
`

const tableHeaderCells: UITypes.TableHeaderCell[] = [
  { title: "Contractor name", name: "contractor_name", type: "sort" },
  { title: "Devices", name: "devices", type: "sort" },
  { title: "Date", name: "date", type: "sort" },
  { title: "Author", name: "username", type: "sort" },
]

interface ReordersListModalProps {
  selectedDateRange: [string, string]
  onClose: () => void
}

const ReordersListModal = ({ selectedDateRange, onClose }: ReordersListModalProps) => {
  const [triggerGetReordersList, { isFetching, data }] = useLazyGetReordersListQuery()

  useEffect(() => {
    const fetchData = async () => {
      try {
        await triggerGetReordersList(
          {
            start_date: selectedDateRange[0],
            end_date: selectedDateRange[1],
          },
          true,
        )
      } catch (error) {
        console.error(error)
      }
    }

    fetchData()
  }, [selectedDateRange])

  const convertReordersToTableRows = (data?: ApiTypes.Res.ReordersData[]): UITypes.TableBodyRow[] => {
    if (!data?.length) return []

    return data.map(
      reorder =>
        ({
          contractor_name: { value: reorder.contractor_name },
          date: { value: reorder.date },
          devices: { value: reorder.devices },
          username: { value: reorder.username || "-" },
          rowSettings: {
            id: reorder.contractor_id,
          },
        }) as UITypes.TableBodyRow,
    )
  }

  const { handleExport } = useExportCSV({
    params: { start_date: selectedDateRange[0], end_date: selectedDateRange[1] },
    headerCells: tableHeaderCells,
    fileName: "reorders",
    convertData: convertReordersToTableRows,
    useLazyQuery: useLazyGetReordersListQuery,
  })

  return (
    <>
      {createPortal(
        <ModalLayout width="640px" isFetching={isFetching} onClose={onClose} isWithoutFooter>
          <ButtonWrapper>
            <ButtonNew
              color="tropicalCyan"
              onClick={handleExport}
              className={styles.tableNavItem}
              disabled={data?.length === 0}
            >
              Export
            </ButtonNew>
          </ButtonWrapper>
          <ModalHeader title="Reorders" />
          <ModalContent>
            <ListContent>
              <Header>
                <Row>
                  {tableHeaderCells.map(cell => (
                    <span key={cell.name}>{cell.title}</span>
                  ))}
                </Row>
              </Header>
              {data?.map(item => (
                <Row key={`${item.contractor_name}_${item.date}`}>
                  <span>{item.contractor_name}</span>
                  <span>{item.devices}</span>
                  <span>{item.date}</span>
                  <span>{item.username}</span>
                </Row>
              ))}
            </ListContent>
          </ModalContent>
        </ModalLayout>,
        document.body,
      )}
    </>
  )
}

export default ReordersListModal
